<script setup lang="ts">
import { inject, ref, computed, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import Fluid from 'primevue/fluid';

const dialogRef = inject('dialogRef') as Ref;
const getPostSearch = dialogRef?.value?.data?.getPostSearch;
const isHiddenChild = !!dialogRef?.value?.data?.isHiddenChild;
const isHiddenInfant = !!dialogRef?.value?.data?.isHiddenInfant;
const onClose = dialogRef?.value?.options.emits.onClose;

const getBaseFare = ref({});
const currentLanguage = getCurrentLocale();

const listTaxes = getPostSearch?.filter((post) => {
  if (post?.airTaxCode == 'FARE_BASE') {
    getBaseFare.value = post;
  } else {
    return post;
  }
});

const getTotalAdtFare = computed(() => {
  let total = 0;
  if (listTaxes) {
    total = listTaxes
      .reduce(function (acc, obj) {
        return acc + (obj.adtFare ? +obj.adtFare : 0);
      }, 0)
      .toFixed(2);
  }
  return total;
});

const getTotalChFare = computed(() => {
  let total = 0;
  if (listTaxes) {
    total = listTaxes
      .reduce(function (acc, obj) {
        return acc + (obj.chFare ? +obj.chFare : 0);
      }, 0)
      .toFixed(2);
  }
  return total;
});

const getTotalInFare = computed(() => {
  let total = 0;
  if (listTaxes) {
    total = listTaxes
      .reduce(function (acc, obj) {
        return acc + (obj.inFare ? +obj.inFare : 0);
      }, 0)
      .toFixed(2);
  }
  return total;
});

const convertPriceLanguage = (price: number) => {
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
};
</script>

<template>
  <div class="price-breakdown-modal">
    <div class="bg-primary lead-option h-3rem pl-5 flex align-items-center font-bold mb-2 text-2xl">{{ $t('common.labels.air_only') }}</div>
    <Fluid class="p-5">
      <div id="transportation-charges" class="field grid">
        <div class="col-12 sm:col-6 font-bold" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">{{ $t('worksheet.air_transportation_charges') }}</div>
        <div class="col-12 sm:col-2 text-right">{{ $t('common.adult') }}</div>
        <div v-if="!isHiddenChild" class="col-12 sm:col-2 text-right">{{ $t('common.child') }}</div>
        <div v-if="!isHiddenInfant" class="col-12 sm:col-2 text-right">{{ $t('common.infant') }}</div>
      </div>
      <Divider />
      <div id="base-fare" class="field grid">
        <div class="col-12 sm:col-6" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">{{ $t('worksheet.base_fare') }}</div>
        <div class="col-12 sm:col-2 text-right">${{ getBaseFare && getBaseFare.adtFare ? convertPriceLanguage(getBaseFare.adtFare) : '0.00' }}</div>
        <div v-if="!isHiddenChild" class="col-12 sm:col-2 text-right">${{ getBaseFare && getBaseFare.chFare ? getBaseFare.chFare : '0.00' }}</div>
        <div v-if="!isHiddenInfant" class="col-12 sm:col-2 text-right">${{ getBaseFare && getBaseFare.inFare ? getBaseFare.inFare : '0.00' }}</div>
      </div>
      <Divider />
      <div id="fees-charges" class="field grid">
        <div class="col-12 sm:col-12 font-bold" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">{{ $t('worksheet.taxes_fees_and_charges') }}</div>
      </div>
      <div id="list-taxes" class="field grid" v-for="(post, index) in listTaxes" :key="index">
        <div class="col-12 sm:col-6" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">
          {{ post.airTaxDes ? post.airTaxDes : '' }} <span class="font-bold">({{ post?.airTaxCode ? post?.airTaxCode : 'Code' }})</span>
        </div>
        <div class="col-12 sm:col-2 text-right">${{ post.adtFare ? convertPriceLanguage(post.adtFare) : '0.00' }}</div>
        <div v-if="!isHiddenChild" class="col-12 sm:col-2 text-right">${{ post.chFare ? post.chFare : '0.00' }}</div>
        <div v-if="!isHiddenInfant" class="col-12 sm:col-2 text-right">${{ post.inFare ? post.inFare : '0.00' }}</div>
      </div>
      <Divider />
      <div id="total-taxes" class="field grid">
        <div class="col-12 sm:col-6 font-bold" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">{{ $t('worksheet.total_taxes') }}</div>
        <div class="col-12 sm:col-2 font-bold text-right">${{ getTotalAdtFare ? convertPriceLanguage(getTotalAdtFare) : '0.00' }}</div>
        <div v-if="!isHiddenChild" class="col-12 sm:col-2 font-bold text-right">${{ getTotalChFare ? getTotalChFare : '0.00' }}</div>
        <div v-if="!isHiddenInfant" class="col-12 sm:col-2 font-bold text-right">${{ getTotalInFare ? getTotalInFare : '0.00' }}</div>
      </div>
      <Divider />
      <div id="grand-total" class="field grid text-lg">
        <div class="col-12 sm:col-6" :class="{ 'sm:col-9': isHiddenChild && isHiddenInfant }">
          <span class="font-bold">{{ $t('worksheet.grand_total') }}</span
          >{{ $t('worksheet.canadian_dollars') }}
        </div>
        <div class="col-12 sm:col-2 font-bold text-right">${{ convertPriceLanguage(+getTotalAdtFare + (getBaseFare && getBaseFare.adtFare ? +getBaseFare.adtFare : 0)) }}</div>
        <div v-if="!isHiddenChild" class="col-12 sm:col-2 font-bold text-right">${{ (+getTotalChFare + (getBaseFare && getBaseFare.chFare ? +getBaseFare.chFare : 0)).toFixed(2) }}</div>
        <div v-if="!isHiddenInfant" class="col-12 sm:col-2 font-bold text-right">${{ (+getTotalInFare + (getBaseFare && getBaseFare.inFare ? +getBaseFare.inFare : 0)).toFixed(2) }}</div>
      </div>
    </Fluid>
    <div class="flex justify-content-end">
      <Button id="btn-close" :label="$t('common.buttons.close')" @click="onClose()" class="p-button-primary mb-2" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.price-breakdown-modal {
  width: min(800px, 90vw);
}
</style>
